import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Divider from 'components/Luxkit/Divider'
import DropdownContainer from 'components/Luxkit/Dropdown/DropdownContainer'
import Dropdown from 'components/Luxkit/Dropdown/v2/Dropdown'
import Caption from 'components/Luxkit/Typography/Caption'
import SearchFormTextField from 'components/SearchV2/Components/SearchFormField/SearchFormTextField'
import { DEFAULT_FLEXIBLE_DURATION_RANGE, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL, LOCATION_SEARCH_INPUT_TITLE_LABEL } from 'constants/search'
import useToggle from 'hooks/useToggle'
import { EmptyArray, take, uniqueBy, without } from 'lib/array/arrayUtils'
import noop from 'lib/noop'
import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import RecentSearchSuggestions from '../SearchForm/SearchLocationInput/RecentSearchSuggestions'
import SearchMenuListItem from '../SearchForm/SearchMenu/SearchMenuListItem'
import SearchPlaceSelectList from './SearchPlaceSelectList'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { connect } from 'react-redux'
import { searchFromHomepageEvent } from 'analytics/optimizely/events'
import * as Analytics from 'analytics/analytics'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'

const PlaceControl = styled.div`
  position: relative;
  width: 100%;
`

interface Props {
  open?: boolean;
  className?: string;
  label?: string;
  value?: App.SearchItem;
  placeholder?: string;
  searchTypes?: Array<App.SearchType>;
  placeTypes?: Array<App.SearchPlaceType>;
  searchVertical?: App.SearchPlaceVertical;
  /** Items to show when a user hasn't entered any search */
  placeholderItems?: Array<App.SearchItem>;
  placeholderTitle?: string;
  /** When the selected item changes */
  onChange?: (item?: App.SearchItem) => void;
  onToggle?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  onResultsChange?: (results: Array<App.SearchItem>, searchPhrase: string) => void;
  inputRef?: React.Ref<HTMLInputElement>;
  required?: boolean;
  /** A list of recent search items, will be displayed along with placeholder items */
  recentSearchItems?: Array<App.FullRecentSearch>;
  /** Use this to supply your own search results list the input will use */
  overrideSearchItems?: Array<App.SearchItem>;
  pathname: string;
  regionCode: string;
  /**
   * Dismiss the dropdown once an item is selected
   *
   * @default true
   */
  closeOnChange?: boolean;
}

function SearchPlaceSelectDropdown(props: Props) {
  const {
    open,
    searchTypes,
    searchVertical,
    placeholder = LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
    className,
    label = LOCATION_SEARCH_INPUT_TITLE_LABEL,
    placeTypes,
    value,
    placeholderItems = EmptyArray,
    onChange = noop,
    onResultsChange = noop,
    placeholderTitle,
    inputRef,
    recentSearchItems = EmptyArray,
    overrideSearchItems,
    closeOnChange = true,
    onToggle,
    required = false,
    pathname,
    regionCode,
  } = props

  const [localRecentSearches, setLocalRecentSearches] = useState<Array<App.FullRecentSearch>>(recentSearchItems)
  const [searchPhrase, setSearchPhrase] = useState<string>(value?.format.mainText ?? '')
  const [selectedDropdown, setSelectedDropdown] = useState<number | undefined>()
  const localInputRef = useRef<HTMLInputElement>(null)
  const [localOpen, , openDropdown, closeDropdown] = useToggle()
  const [results, setResults] = useState<Array<App.SearchItem>>([])
  const finalOpen = !!(open ?? localOpen)
  const dispatch = useAppDispatch()

  useEffect(() => {
    setLocalRecentSearches(recentSearchItems)
  }, [recentSearchItems])

  const handleOpen = useCallback((e) => {
    openDropdown()
    if (onToggle) {
      onToggle(e)
    }
  }, [onToggle, openDropdown])

  useImperativeHandle(inputRef, () => localInputRef.current!)
  const onLocationSelect = useCallback((searchItem?: App.SearchItem) => {
    setSearchPhrase(searchItem?.format.mainText ?? '')
    onChange(searchItem)
    if (closeOnChange) {
      closeDropdown()
    }
  }, [onChange, closeOnChange, closeDropdown])

  const resultsChange = useCallback((results: Array<App.SearchItem>, searchPhrase: string) => {
    setResults(results)
    onResultsChange(results, searchPhrase)
  }, [onResultsChange])

  const showPlaceholder = searchPhrase.length === 0 || (value && value.format.mainText === searchPhrase)
  const hasRecentSearchItems = !!localRecentSearches?.length

  const [recentSearches, placeholdersSearches] = useMemo(() => {
    const uniqueSearchItems = take(uniqueBy(localRecentSearches, item => item.searchItem.value), 3)
    return [
      uniqueSearchItems,
      // max of 7 items total in the list. We only take up to 3 recent items, so take remainder as placeholder items
      take(placeholderItems, 7 - uniqueSearchItems.length),
    ]
  }, [localRecentSearches, placeholderItems])

  const onRecentSearchDelete = useCallback((item: App.FullRecentSearch) => {
    setLocalRecentSearches(without(localRecentSearches, item))
  }, [localRecentSearches])

  const onSearchPhraseChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchPhrase(e.currentTarget.value)
  }, [])

  const onClearClick = useCallback(() => {
    setSearchPhrase('')
    onChange(undefined)
  }, [onChange])

  const onRecentSearchClick = useCallback((recentSearch: App.FullRecentSearch) => {
    const { dates, rooms } = recentSearch
    if (pathname === `/${regionCode.toLowerCase()}`) {
      Analytics.track('search-from-homepage', searchFromHomepageEvent())
    }

    const newSearch = encodeSearchParams({
      flexibleMonths: dates?.flexibleMonths,
      flexibleNights: dates?.flexibleDuration as DEFAULT_FLEXIBLE_DURATION_RANGE,
      dates: {
        checkIn: dates.checkIn,
        checkOut: dates.checkOut,
      },
      urlSearch: '',
      rooms,
      searchItem: recentSearch.searchItem,
      userSelectedFlexibleMonths: !!dates?.flexibleMonths,
    }).toString()
    const path = pathname.includes('/map') ? '/search/map' : '/search'
    closeDropdown()
    dispatch(pushWithRegion(path, newSearch))
  }, [dispatch, closeDropdown, pathname, regionCode])

  const onKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedDropdown(prev => prev ? prev - 1 : undefined)
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedDropdown(prev => prev === undefined ? 0 : prev + 1)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedDropdown === undefined) {
        if (showPlaceholder) {
          onRecentSearchClick(recentSearches[0])
        } else {
          onLocationSelect(results[0])
        }
      } else {
        if (showPlaceholder && selectedDropdown < recentSearches.length) {
          onRecentSearchClick(recentSearches[selectedDropdown])
        } else if (showPlaceholder && selectedDropdown > recentSearches.length - 1) {
          onLocationSelect(placeholdersSearches[selectedDropdown - recentSearches.length])
        } else if (!showPlaceholder) {
          onLocationSelect(results[selectedDropdown])
        }
      }
      setSelectedDropdown(undefined)
    }
  }, [onLocationSelect, onRecentSearchClick, placeholdersSearches, recentSearches, results, selectedDropdown, showPlaceholder])

  return <PlaceControl className={className} onKeyDown={onKeyDown}>
    <HiddenInput
      required={required}
      value={value?.format.mainText}
    />
    <SearchFormTextField
      label={label}
      placeholder={placeholder}
      ref={localInputRef}
      value={searchPhrase}
      displayValue={value?.format.mainText ?? ''}
      onChange={onSearchPhraseChange}
      onFocus={handleOpen}
      onClear={onClearClick}
    />
    <Dropdown
      open={finalOpen}
      triggerRef={localInputRef}
      floatingStrategy="absolute"
      width="100%"
      disableFlip
      onClose={closeDropdown}
    >
      <DropdownContainer>
        {showPlaceholder && <VerticalSpacer gap={12}>
          {hasRecentSearchItems && <>
            <RecentSearchSuggestions
              recentSearches={recentSearches}
              onDelete={onRecentSearchDelete}
              selectedDropdown={selectedDropdown}
              onRecentSearchClick={onRecentSearchClick}
            />
            <Divider kind="primary" />
          </>}
          <div>
            {placeholderTitle && <Caption variant="large" weight="bold">{placeholderTitle}</Caption>}
            {placeholdersSearches.map((item, index) => <SearchMenuListItem
              key={item.value}
              searchItem={item}
              onClick={onLocationSelect}
              selected={index + recentSearches.length === selectedDropdown}
            />)}
          </div>
        </VerticalSpacer>}
        {!showPlaceholder &&
          <SearchPlaceSelectList
            searchPhrase={searchPhrase}
            onResultsChange={resultsChange}
            onChange={onLocationSelect}
            placeTypes={placeTypes}
            searchTypes={searchTypes}
            vertical={searchVertical}
            selectedDropdown={selectedDropdown}
            overrideSearchItems={overrideSearchItems}
          />}
      </DropdownContainer>
    </Dropdown>

  </PlaceControl>
}

function mapStateToProps(state: App.State) {
  return {
    pathname: state.router.location.pathname,
    regionCode: state.geo.currentRegionCode,
  }
}

export default connect(mapStateToProps)(SearchPlaceSelectDropdown)
